import React from "react";

/**
 * FullscreenController - Handles game iframe fullscreen across iOS, Android, and Desktop
 * 
 * Key Features:
 * - iOS: CSS-based rotation for landscape games in portrait mode
 * - Android: Native fullscreen API with orientation lock
 * - Desktop: Standard fullscreen with manual controls
 * 
 * Required HTML structure:
 * - #iframe-div (container)
 * - #float_button (exit button)
 * - #game_frame (iframe)
 * - #img_frame (placeholder image)
 * - #start-btn (start button)
 * - .bottom-strip (controls strip)
 */

class FullscreenController {
    constructor() {
        this.container = null;
        this.float_button = null;
        this.game_frame = null;
        this.img_frame = null;
        this.start_btn = null;
        this.bottom_strip = null;
        this.refreshElements();
        
        this.os = this.getOS();
        this.iframe_height = 600;
        this.isRotated = false;
        this.orientationListener = null;
        this.iosViewportListener = null;
    }

    refreshElements() {
        this.container = document.getElementById("iframe-div");
        this.float_button = document.getElementById("float_button");
        this.game_frame = document.getElementById("game_frame");
        this.img_frame = document.getElementById("img_frame");
        this.start_btn = document.getElementById("start-btn");
        this.bottom_strip = document.getElementsByClassName("bottom-strip")[0];
    }

    /**
     * Detects device OS
     * @returns {string} "iOS", "iOS Tablet", "Android", "Android Tablet", or "Unknown"
     */
    getOS() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

        if (/android/i.test(userAgent)) {
            if (!/mobile/i.test(userAgent) || Math.min(screen.width, screen.height) >= 600) {
                return "Android Tablet";
            }
            return "Android";
        }

        if (isIOS) {
            if (/iPad/.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
                return "iOS Tablet";
            }
            return "iOS";
        }

        return "Unknown";
    }

    /**
     * Main initialization function - entry point for starting the game
     * @param {Object} params - Configuration object
     * @param {string} params.GetOS - Operating system override
     * @param {string} params.orientation - Game orientation requirement ("landscape" or "portrait")
     */
    func_int({ GetOS, orientation = "landscape" } = {}) {
        this.refreshElements();
        if (!this.container || !this.game_frame) return;

        const os = GetOS || this.os;
        const isPortrait = window.matchMedia("(orientation: portrait)").matches;
        const needsLandscape = orientation === "landscape";
        // console.log("func_int called:", { os, orientation, isPortrait, needsLandscape });

        // Common setup for all platforms
        this.game_frame.style.display = "block";
        this.container.style.display = "block";
        if (this.img_frame) this.img_frame.style.display = "none";
        if (this.start_btn) this.start_btn.style.display = "none";

        // iOS handling
        if (os === "iOS" || os === "iOS Tablet") {
            // Initial check
            this.checkOrientationAndGuard(orientation);

            // remove the previous listener if it exists to prevent multiple listeners
            if (this.orientationListener) {
                window.removeEventListener("orientationchange", this.orientationListener);
            }

            this.orientationListener = () => {
                this.checkOrientationAndGuard(orientation);
            };

            window.addEventListener("orientationchange", this.orientationListener);
            window.addEventListener("resize", this.orientationListener);

            this.handleIOS(orientation, isPortrait, needsLandscape);
        }
        // Android handling
        else if (os === "Android" || os === "Android Tablet") {
            this.handleAndroid(needsLandscape);
        }
        // Desktop handling
        else {
            this.handleDesktop();
        }
    }

    /**
     * iOS-specific handling with CSS rotation
     */
    handleIOS(orientation, isPortrait, needsLandscape) {
        this.refreshElements();
        if (!this.container || !this.game_frame) return;

        // If game needs landscape but device is portrait, apply CSS rotation
        this.game_frame.style.display = "block";
        this.container.style.display = "block";
        if (this.img_frame) this.img_frame.style.display = "none";
        // Guard/overlay handles wrong orientation state. Keep container fullscreen.
        this.container.classList.add('fullscreen_manager');
        this.applyIOSViewportSize();

        // Show float button and lock scroll
        if (this.float_button) {
            this.float_button.style.display = "flex";
            this.float_button.classList.add('float_button_visibility');
        }
        this.lockBodyScroll(true);

        if (this.iosViewportListener) {
            window.removeEventListener("resize", this.iosViewportListener);
            window.removeEventListener("orientationchange", this.iosViewportListener);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", this.iosViewportListener);
            }
        }
        this.iosViewportListener = () => this.applyIOSViewportSize();
        window.addEventListener("resize", this.iosViewportListener);
        window.addEventListener("orientationchange", this.iosViewportListener);
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", this.iosViewportListener);
        }

        // Scroll to hide address bar
        setTimeout(() => window.scrollTo(0, 1), 300);
    }

    applyIOSViewportSize() {
        this.refreshElements();
        if (!this.container || !this.game_frame) return;
        const vh = window.visualViewport?.height || window.innerHeight;
        this.container.style.height = `${Math.max(vh, 320)}px`;
        this.container.style.width = "100vw";
        this.game_frame.style.height = "100%";
        this.game_frame.style.width = "100%";
    }

    /**
     * Apply CSS rotation for landscape games on portrait iOS devices
     */
    applyIOSLandscapeRotation() {
        this.refreshElements();
        if (!this.container || !this.game_frame) return;

        this.isRotated = true;
        this.container.classList.add('fullscreen_manager');
        
        // Apply rotation transform
        this.container.style.setProperty('transform', 'rotate(90deg)', 'important');
        this.container.style.setProperty('transform-origin', 'center center', 'important');
        this.container.style.setProperty('width', `${window.innerHeight}px`, 'important');
        this.container.style.setProperty('height', `${window.innerWidth}px`, 'important');
        this.container.style.setProperty('position', 'fixed', 'important');
        this.container.style.setProperty('top', '50%', 'important');
        this.container.style.setProperty('left', '50%', 'important');
        this.container.style.setProperty('transform', 'rotate(90deg) translate(-50%, -50%)', 'important');
        this.container.style.setProperty('margin-left', `${-window.innerHeight / 2}px`, 'important');
        this.container.style.setProperty('margin-top', `${-window.innerWidth / 2}px`, 'important');
        // Ensure iframe takes full rotated space
        // this.game_frame.style.setProperty('width', '100%', 'important');
        // this.game_frame.style.setProperty('height', '100%', 'important');
        // this.game_frame.style.setProperty('pointer-events', 'auto', 'important');
        // this.game_frame.style.setProperty('touch-action', 'auto', 'important');
    }

    /**
     * Remove CSS rotation for iOS
     */
    removeIOSRotation() {
        this.refreshElements();
        if (!this.container) return;

        this.isRotated = false;
        const propertiesToRemove = [
            'transform', 'transform-origin', 'width', 'height',
            'position', 'top', 'left', 'margin-left', 'margin-top'
        ];
        
        propertiesToRemove.forEach(prop => {
            this.container.style.removeProperty(prop);
        });
        
        this.container.classList.add('fullscreen_manager');
    }

    /**
     * Handle orientation change for iOS devices
     */
    onOrientationChange(gameOrientation = "landscape") {
        const os = this.getOS();
        if (os !== "iOS" && os !== "iOS Tablet") return;
        
        const angle = window.orientation || screen.orientation?.angle || 0;
        const isLandscape = (angle === 90 || angle === -90);
        const needsLandscape = gameOrientation === "landscape";

        // console.log("Orientation changed:", { angle, isLandscape, needsLandscape });

        if (needsLandscape && !isLandscape) {
            // Device rotated to portrait, but game needs landscape
            // this.applyIOSLandscapeRotation();
        } else if (needsLandscape && isLandscape) {
            // Device is landscape, game needs landscape - perfect match
            this.removeIOSRotation();
        } else if (!needsLandscape && !isLandscape) {
            // Device is portrait, game needs portrait - perfect match
            this.removeIOSRotation();
        }
    }

    /**
     * Android-specific handling with native fullscreen API
     */
    handleAndroid(needsLandscape) {
        this.EnterFullscreen(needsLandscape ? 'landscape' : null);
    }

    /**
     * Desktop handling
     */
    handleDesktop() {
        if (this.bottom_strip) this.bottom_strip.style.display = "block";
        // this.float_button.style.display = "flex";
        // this.float_button.classList.add('float_button_visibility');
    }

    /**
     * Enter fullscreen mode (primarily for Android and Desktop)
     * @param {string|null} lockOrientation - Orientation to lock ("landscape" or "portrait")
     */
    EnterFullscreen(lockOrientation = null) {
        this.refreshElements();
        if (!this.container || !this.game_frame) return;

        const iframe = this.container;

        const onSuccess = () => {
            // Resize game frame
            this.game_frame.style.width = "100%";
            this.game_frame.style.height = "100%";
            this.game_frame.style.visibility = "visible";
            
            // Show float button
            if (this.float_button) {
                this.float_button.style.display = "flex";
                this.float_button.style.visibility = "visible";
                this.float_button.classList.add('float_button_visibility');
            }

            // Lock orientation if specified (Android only)
            if (lockOrientation) {
                this.lockOrientation(lockOrientation);
            }

            // Focus iframe
            this.game_frame.contentWindow?.focus();
        };

        try {
            // Try different fullscreen APIs
            if (iframe.requestFullscreen) {
                iframe.requestFullscreen().then(onSuccess).catch(console.warn);
            } else if (iframe.webkitRequestFullscreen) {
                iframe.webkitRequestFullscreen();
                onSuccess();
            } else if (iframe.mozRequestFullScreen) {
                iframe.mozRequestFullScreen();
                onSuccess();
            } else if (iframe.msRequestFullscreen) {
                iframe.msRequestFullscreen();
                onSuccess();
            } else if (iframe.webkitEnterFullscreen) {
                iframe.webkitEnterFullscreen();
                onSuccess();
            } else {
                // Fallback for desktop
                onSuccess();
            }
        } catch (error) {
            console.warn("Fullscreen request failed:", error);
            onSuccess(); // Still set up the UI
        }
    }

    /**
     * Lock screen orientation (Android)
     * @param {string} orientation - "landscape" or "portrait"
     */
    lockOrientation(orientation = 'landscape') {
        if (!screen.orientation?.lock) return;

        const orientationMap = {
            'landscape': 'landscape-primary',
            'portrait': 'portrait-primary'
        };

        const lockValue = orientationMap[orientation] || orientation;

        screen.orientation.lock(lockValue).catch(err => {
            console.warn('Failed to lock orientation:', err);
            // Fallback to generic orientation
            screen.orientation.lock(orientation).catch(e => {
                console.warn('Orientation lock fallback failed:', e);
            });
        });
    }

    /**
     * Exit fullscreen and reset to initial state
     * @param {Object} params
     * @param {boolean} params.forceExit - Force exit even if user didn't initiate
     */
    ExitFullscreen({ forceExit = false } = {}) {
        this.refreshElements();
        if (!this.container || !this.game_frame) return;

        // console.log("ExitFullscreen called, forceExit:", forceExit);
        
        // clear orientation listeners FIRST
        this.clearOrientationListener();
        if (this.iosViewportListener) {
            window.removeEventListener("resize", this.iosViewportListener);
            window.removeEventListener("orientationchange", this.iosViewportListener);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", this.iosViewportListener);
            }
            this.iosViewportListener = null;
        }

        // Unlock body scroll
        this.lockBodyScroll(false);

        // Hide and reset float button
        if (this.float_button) {
            this.float_button.style.display = "none";
            this.float_button.classList.remove('float_button_visibility');
        }

        const platform = this.getOS();

        // Mobile platforms (iOS/Android)
        if (platform === "iOS" || platform === "iOS Tablet" || 
            platform === "Android" || platform === "Android Tablet") {
            
            // Remove iOS rotation if applied
            if (this.isRotated) {
                this.removeIOSRotation();
            }

            // Reset container
            this.container.classList.remove('fullscreen_manager');
            this.container.style.display = "none";
            this.container.style.removeProperty("height");
            this.container.style.removeProperty("width");
            
            // Clear iframe
            this.game_frame.src = "about:blank";
            
            // Show original image
            if (this.img_frame) this.img_frame.style.display = "block";
            if (this.start_btn) this.start_btn.style.display = "block";
        }
        // Desktop
        else {
            // Reset iframe size
            this.game_frame.style.width = "100%";
            this.game_frame.style.height = `${this.iframe_height}px`;
            
            // Show bottom strip
            if (this.bottom_strip) this.bottom_strip.style.display = "block";
            
            // Focus iframe
            this.game_frame.contentWindow?.focus();
        }

        // Force exit fullscreen API if requested
        if (forceExit && (document.fullscreenElement || document.webkitFullscreenElement ||
            document.mozFullScreenElement || document.msFullscreenElement)) {
            
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(console.warn);
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    }

    /**
     * Lock/unlock body scroll (iOS)
     * @param {boolean} lock - true to lock, false to unlock
     */
    lockBodyScroll(lock) {
        if (lock) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
        }
    }

    /*
        * Additional utility methods can be added here, such as:
        * - Method to deduct orientation if landscape/portrait is not explicitly provided 
     */

    DeduceOrientation() {
        const width = window.innerWidth;
        const height = window.innerHeight;
        return width > height ? "landscape" : "portrait";
    }

    checkOrientationAndGuard(gameOrientation = "landscape") {
        this.refreshElements();
        if (!this.game_frame) return;

        const isPortrait = window.matchMedia("(orientation: portrait)").matches;

        const wrong =
            (gameOrientation === "landscape" && isPortrait) ||
            (gameOrientation === "portrait" && !isPortrait);

        const overlay = document.getElementById("rotate-overlay");

        if (wrong) {
            overlay?.classList.add("show");
            this.game_frame.style.pointerEvents = "none";
            this.game_frame.style.visibility = "hidden";
        } else {
            overlay?.classList.remove("show");
            this.game_frame.style.pointerEvents = "auto";
            this.game_frame.style.visibility = "visible";
            this.game_frame.contentWindow?.focus();
        }
}
    clearOrientationListener() {
        if (!this.orientationListener) return;

        window.removeEventListener(
            "orientationchange",
            this.orientationListener
        );

        window.removeEventListener(
            "resize",
            this.orientationListener
        );

        this.orientationListener = null;
    }


}

export default FullscreenController;
